/**
 * Layout de l'espace partenaire — bénéficiaire Affiliation.
 *
 * Miroir strict de `PartnerLayout` (Royalties) : même en-tête, même
 * pied de page, même topbar sans sidebar. Seule la palette et le libellé
 * changent (emerald/teal + « Espace partenaire affilié »).
 */
import React from "react";
import { LogOut, ChevronDown, Sparkles } from "lucide-react";
import { Outlet, useNavigate } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { PLATFORM_TAGLINE } from "../lib/config";
import BrandMark from "./BrandMark";
import AppFooter from "./AppFooter";
import ForcePasswordChangeDialog from "./ForcePasswordChangeDialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "./ui/dropdown-menu";

const ROLE_LABELS = {
  partenaire: "Partenaire Affilié",
};

export default function AffiliateLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50" data-testid="affiliate-layout">
      {/* Bande d'entête : logo Plania + tricolore + tagline (identique au layout principal) */}
      <div className="sticky top-0 z-30 bg-white shadow-sm">
        <div className="h-16 px-4 sm:px-6 grid grid-cols-3 items-center">
          <div />
          <div className="justify-self-center">
            <BrandMark size="xl" monochrome uppercase />
          </div>
          <div />
        </div>
        <div className="h-1 flex" aria-hidden="true" data-testid="brand-tricolor">
          <span className="flex-1 bg-[#0055A4]" />
          <span className="flex-1 bg-white" />
          <span className="flex-1 bg-[#EF4135]" />
        </div>
        <div className="px-4 sm:px-6 py-1.5 bg-zinc-50/60 text-center">
          <span
            className="text-[11px] uppercase tracking-[0.18em] text-zinc-500"
            data-testid="platform-tagline-banner"
          >
            {PLATFORM_TAGLINE}
          </span>
        </div>
      </div>

      {/* Topbar allégée : titre espace + date + user menu */}
      <header
        className="h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4 sm:px-6 sticky top-[112px] z-20"
        data-testid="affiliate-header"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-7 w-7 rounded-sm bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center shrink-0">
            <Sparkles size={14} strokeWidth={1.8} className="text-white" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono leading-none">
              Espace partenaire affilié
            </p>
            <p className="text-sm font-semibold text-zinc-900 truncate leading-tight">
              {user?.name || user?.email}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <p className="font-mono text-xs text-zinc-500 hidden md:block">
            {(() => {
              const d = new Date();
              const wd = d.toLocaleDateString("fr-FR", { weekday: "long" });
              const dd = String(d.getDate()).padStart(2, "0");
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              return `${wd} ${dd}/${mm}/${d.getFullYear()}`;
            })()}
          </p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 p-1.5 hover:bg-zinc-50 rounded-sm text-left"
                data-testid="affiliate-user-menu-trigger"
              >
                <div className="h-8 w-8 bg-zinc-900 text-white flex items-center justify-center text-xs font-medium">
                  {(user?.name || "P").slice(0, 2).toUpperCase()}
                </div>
                <div className="hidden sm:block min-w-0">
                  <p className="text-[11px] text-zinc-500 truncate font-mono leading-none">
                    {ROLE_LABELS[user?.role] || user?.role}
                  </p>
                </div>
                <ChevronDown size={14} className="text-zinc-400 flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-mono text-xs">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="affiliate-logout-btn"
                className="text-red-600 focus:text-red-700 focus:bg-red-50"
              >
                <LogOut size={13} className="mr-2" />
                Se déconnecter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main
        className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 animate-fade-in-up"
        data-testid="main-content"
      >
        <Outlet />
      </main>

      <AppFooter />
      <ForcePasswordChangeDialog />
    </div>
  );
}
